<script>
import ListHeader from '@/components/common/ListHeader.vue';
import FilterChips from '@/components/common/FilterChips.vue';
import ListPagination from '@/components/common/ListPagination.vue';
import ProSurveyFilters from '@/components/surveys/ProSurveyFilters.vue';
import ProSurveySummary from '@/components/surveys/ProSurveySummary.vue';
import ProSurveyTable from '@/components/surveys/ProSurveyTable.vue';
import DeleteSurveyModal from '@/components/surveys/DeleteSurveyModal.vue';
import surveyService from '@/shared/services/survey.service';
import proDashboardService from '@/shared/services/pro-dashboard.service';
import notificationService from '@/shared/services/notification.service';
import {
  countActiveFilters,
  fromCriteria,
  toCriteria,
} from '@/shared/utils/list-filters';

// Eight rows, not ten: with the summary cards above it, a ten-row page is
// taller than the professional layout's fixed-height content area. The page
// size selector still offers 10, 25 and 50.
const EMPTY_FILTERS = {
  searchQuery: '',
  status: 'ALL',
  governorates: [],
  sectors: [],
  referenceYears: [],
  hasRespondent: '',
  interviewDate: { from: null, to: null },
  dateAdded: { from: null, to: null },
  perPage: 8,
};

/*
  Le nombre de lignes par page regle l'affichage, pas le contenu: le compter
  comme un critere ferait grimper la pastille du panneau sans qu'aucune ligne
  ne soit ecartee, et l'enregistrer imposerait cette taille a qui rappellerait
  le prereglage.
*/
const CRITERIA_FILTERS = Object.fromEntries(
  Object.entries(EMPTY_FILTERS).filter(([key]) => key !== 'perPage'),
);

const STATUS_LABELS = { SUBMITTED: 'Soumise', DRAFT: 'Brouillon' };

export default {
  name: 'ProListSurveys',
  components: {
    ListHeader,
    FilterChips,
    ListPagination,
    ProSurveyFilters,
    ProSurveySummary,
    ProSurveyTable,
    DeleteSurveyModal,
  },
  data() {
    return {
      surveys: [],
      loading: false,
      error: null,
      exporting: false,
      filterTimeout: null,
      stats: {
        totalSurveys: 0,
        completedSurveys: 0,
        draftSurveys: 0,
      },
      filters: { ...EMPTY_FILTERS },
      filterOptions: { governorates: [], sectors: [], referenceYears: [] },
      panelOpen: false,
      appliedFilterId: null,
      currentPage: 1,
      totalSurveys: 0,
      totalPages: 0,
      showDeleteModal: false,
      surveyToDelete: null,
    };
  },
  computed: {
    perPage() {
      return this.filters.perPage;
    },

    activeCount() {
      return countActiveFilters(this.filters, CRITERIA_FILTERS);
    },

    /** Ce qu'on enregistre sous un nom: les criteres qui filtrent, rien d'autre. */
    criteria() {
      return toCriteria(this.filters, CRITERIA_FILTERS);
    },

    chips() {
      const out = [];
      const f = this.filters;

      if (f.searchQuery) {
        out.push({
          key: 'searchQuery',
          label: `Recherche : ${f.searchQuery}`,
        });
      }
      if (f.status !== 'ALL') {
        out.push({
          key: 'status',
          label: `Statut : ${STATUS_LABELS[f.status] || f.status}`,
        });
      }
      if (f.governorates?.length) {
        out.push({
          key: 'governorates',
          label: `Gouvernorats : ${f.governorates.join(', ')}`,
        });
      }
      if (f.sectors?.length) {
        out.push({
          key: 'sectors',
          label: `Secteurs : ${f.sectors.join(', ')}`,
        });
      }
      if (f.referenceYears?.length) {
        out.push({
          key: 'referenceYears',
          label: `Exercices : ${f.referenceYears.join(', ')}`,
        });
      }
      if (f.hasRespondent) {
        out.push({
          key: 'hasRespondent',
          label:
            f.hasRespondent === 'yes'
              ? 'Répondant renseigné'
              : 'Répondant non renseigné',
        });
      }
      if (f.interviewDate?.from || f.interviewDate?.to) {
        out.push({
          key: 'interviewDate',
          label: `Entretien ${this.rangeLabel(f.interviewDate)}`,
        });
      }
      if (f.dateAdded?.from || f.dateAdded?.to) {
        out.push({
          key: 'dateAdded',
          label: `Création ${this.rangeLabel(f.dateAdded)}`,
        });
      }

      return out;
    },

    hasFilters() {
      return this.chips.length > 0;
    },
  },
  mounted() {
    this.fetchStats();
    this.fetchSurveys();
    this.fetchFilterOptions();
  },
  beforeUnmount() {
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
  },
  methods: {
    /** « du … au … », « depuis le … » ou « jusqu'au … » selon les bornes posees. */
    rangeLabel(range) {
      if (range.from && range.to) {
        return `du ${range.from} au ${range.to}`;
      }

      return range.from ? `depuis le ${range.from}` : `jusqu'au ${range.to}`;
    },

    /** Les criteres actifs, traduits en parametres que l'API lit. */
    queryParams() {
      const f = this.filters;

      const params = {
        createdByMe: true,
        searchText: f.searchQuery || undefined,
        status: f.status !== 'ALL' ? f.status : undefined,
        governorates: f.governorates?.length ? f.governorates : undefined,
        sectors: f.sectors?.length ? f.sectors : undefined,
        referenceYears: f.referenceYears?.length ? f.referenceYears : undefined,
        hasRespondent: f.hasRespondent ? f.hasRespondent === 'yes' : undefined,
        dateFrom: f.interviewDate?.from || undefined,
        dateTo: f.interviewDate?.to || undefined,
        createdAtFrom: f.dateAdded?.from || undefined,
        createdAtTo: f.dateAdded?.to || undefined,
      };

      Object.keys(params).forEach((key) => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });

      return params;
    },

    /**
     * Ce que les choix multiples proposent vient des enquetes du compte
     * lui-meme: lui proposer les gouvernorats de tout le monde afficherait des
     * choix qui ne rendent rien. Un echec laisse les listes vides plutot que
     * de bloquer l'ecran.
     */
    async fetchFilterOptions() {
      try {
        const options = await surveyService.getFilterOptions();

        this.filterOptions = {
          governorates: options.governorates || [],
          sectors: options.sectors || [],
          referenceYears: options.referenceYears || [],
        };
      } catch (err) {
        this.filterOptions = {
          governorates: [],
          sectors: [],
          referenceYears: [],
        };
      }
    },

    async fetchStats() {
      try {
        const response = await proDashboardService.getStats();
        this.stats = {
          totalSurveys: response.totalSurveys || 0,
          completedSurveys: response.completedSurveys || 0,
          draftSurveys: response.draftSurveys || 0,
        };
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    },

    async fetchSurveys() {
      this.loading = true;
      this.error = null;

      try {
        const response = await surveyService.getSurveys({
          page: this.currentPage,
          limit: this.filters.perPage,
          ...this.queryParams(),
          'order[field]': 'createdAt',
          'order[direction]': 'desc',
        });
        this.surveys = response.data || [];
        this.totalSurveys = response.meta?.total || 0;
        this.totalPages = response.meta?.totalPages || 1;
      } catch (err) {
        const friendly = this.formatApiError(err);
        this.error = friendly.message;

        if (friendly.validationMessages?.length) {
          this.showValidationToast(friendly.validationMessages.join(' — '));
        } else {
          this.showErrorToast(friendly.message);
        }

        this.surveys = [];
        this.totalSurveys = 0;
        this.totalPages = 0;
      } finally {
        this.loading = false;
      }
    },

    /**
     * The summary tiles and the filter row both write here. Text input is
     * debounced; a tile or a select applies at once.
     */
    onFilterChange(newFilters) {
      const searchChanged = newFilters.searchQuery !== this.filters.searchQuery;
      this.filters = { ...newFilters };
      this.currentPage = 1;

      // Une main sur les criteres s'ecarte du prereglage rappele: la liste
      // deroulante ne doit plus pretendre le montrer.
      this.appliedFilterId = null;

      if (this.filterTimeout) {
        clearTimeout(this.filterTimeout);
      }

      if (searchChanged) {
        this.filterTimeout = setTimeout(() => this.fetchSurveys(), 400);
      } else {
        this.fetchSurveys();
      }
    },

    /** Rappelle un prereglage: ses criteres sont reposes, la liste se relance. */
    applySavedFilter(filter) {
      if (!filter) {
        return;
      }

      if (this.filterTimeout) {
        clearTimeout(this.filterTimeout);
      }

      // Le nombre de lignes par page n'est pas un critere: celui de l'ecran
      // reste en place.
      this.filters = {
        ...fromCriteria(filter.criteria, CRITERIA_FILTERS),
        perPage: this.filters.perPage,
      };
      this.appliedFilterId = filter.id;
      this.panelOpen = countActiveFilters(this.filters, CRITERIA_FILTERS) > 0;
      this.currentPage = 1;
      this.fetchSurveys();
    },

    /**
     * Le prereglage marque par defaut s'applique a l'ouverture, mais jamais
     * par-dessus des criteres deja regles a la main.
     */
    onSavedFiltersLoaded(defaultFilter) {
      if (defaultFilter && this.activeCount === 0) {
        this.applySavedFilter(defaultFilter);
      }
    },

    clearFilter(key) {
      this.filters = { ...this.filters, [key]: EMPTY_FILTERS[key] };
      this.appliedFilterId = null;
      this.currentPage = 1;
      this.fetchSurveys();
    },

    clearAllFilters() {
      this.filters = { ...EMPTY_FILTERS, perPage: this.filters.perPage };
      this.appliedFilterId = null;
      this.currentPage = 1;
      this.fetchSurveys();
    },

    changePage(page) {
      this.currentPage = page;
      this.fetchSurveys();
    },

    editSurvey(id) {
      this.$router.push({ name: 'survey-edit', params: { id } });
    },

    startSurvey() {
      this.$router.push({ name: 'Survey' });
    },

    formatApiError(error) {
      if (!error) {
        return { message: 'Erreur inconnue', validationMessages: [] };
      }

      if (error.response?.data) {
        const d = error.response.data;

        if (Array.isArray(d.message)) {
          return {
            message: d.error || 'Erreur de validation',
            validationMessages: d.message,
          };
        }
        if (typeof d.message === 'string') {
          return { message: d.message, validationMessages: [] };
        }

        return { message: d.error || 'Erreur serveur', validationMessages: [] };
      }

      if (error.message) {
        return { message: error.message, validationMessages: [] };
      }

      return { message: 'Erreur inconnue', validationMessages: [] };
    },

    showErrorToast(msg) {
      notificationService.toastError(msg);
    },

    showValidationToast(msg) {
      notificationService.toastInfo(msg);
    },

    async exportSurveys() {
      if (this.exporting) {
        return;
      }

      this.exporting = true;

      try {
        const blob = await surveyService.exportSurveys({
          page: 1,
          limit: 10000,
          ...this.queryParams(),
          'order[field]': 'createdAt',
          'order[direction]': 'desc',
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `mes_enquetes_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.xlsx`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        const friendly = this.formatApiError(err);
        this.showErrorToast(
          "Erreur lors de l'exportation : " + friendly.message,
        );
      } finally {
        this.exporting = false;
      }
    },

    openDeleteModal(survey) {
      this.surveyToDelete = survey;
      this.showDeleteModal = true;
    },

    onSurveyDeleted(deletedId) {
      this.surveys = this.surveys.filter((s) => s.id !== deletedId);

      if (this.surveys.length === 0 && this.currentPage > 1) {
        this.currentPage -= 1;
      }

      this.fetchStats();
      this.fetchSurveys();
    },
  },
};
</script>

<template>
  <div class="survey-list-container">
    <ProSurveySummary
      :stats="stats"
      :active="filters.status"
      @select="onFilterChange({ ...filters, status: $event })"
    />

    <div class="list-card">
      <ListHeader title="Mes enquêtes">
        <template #actions>
          <button
            type="button"
            class="list-btn list-btn--primary"
            @click="startSurvey"
          >
            <i class="ri-add-line"></i>
            Nouvelle enquête
          </button>
          <button
            type="button"
            class="list-btn list-btn--ghost"
            title="Exporter mes enquêtes"
            :disabled="exporting"
            @click="exportSurveys"
          >
            <i class="ri-file-excel-line"></i>
            {{ exporting ? 'Export…' : 'Exporter' }}
          </button>
        </template>
      </ListHeader>

      <ProSurveyFilters
        v-model:expanded="panelOpen"
        :filters="filters"
        :options="filterOptions"
        :active-count="activeCount"
        :criteria="criteria"
        :applied-filter-id="appliedFilterId"
        @filter-change="onFilterChange"
        @reset="clearAllFilters"
        @apply-saved="applySavedFilter"
        @saved-filters-loaded="onSavedFiltersLoaded"
      />

      <FilterChips
        :chips="chips"
        @clear="clearFilter"
        @clear-all="clearAllFilters"
      />

      <ProSurveyTable
        :surveys="surveys"
        :loading="loading"
        :error="error"
        :has-filters="hasFilters"
        @edit="editSurvey"
        @delete="openDeleteModal"
        @clear-filters="clearAllFilters"
      />

      <ListPagination
        :current-page="currentPage"
        :per-page="perPage"
        :total="totalSurveys"
        :total-pages="totalPages"
        noun="enquêtes"
        @page-change="changePage"
      />
    </div>

    <DeleteSurveyModal
      v-model="showDeleteModal"
      :submission="surveyToDelete"
      @deleted="onSurveyDeleted"
    />
  </div>
</template>
